import {
  ClientType,
  isContentScript,
  isBackgroundWorker,
  isBackgroundPage,
  isInPopup,
  isExtensionContext,
} from "@common/services/detect-context";

/** sessionStorage key holding the per-surface (per-tab) stable MQTT instance id. */
export const MQTT_INSTANCE_ID_KEY = "bt-mqtt-instance-id";

/**
 * Storage + id-generation seam, injected so the resolver is testable under the
 * repo's `testEnvironment: 'node'` (no sessionStorage/localStorage/crypto globals).
 */
export interface InstanceIdStore {
  getSession(key: string): string | null;
  setSession(key: string, value: string): void;
  getLocal(key: string): string | null;
  genId(): string;
}

/**
 * Keep only characters safe to embed in an MQTT topic segment
 * (the id goes into `users/{uid}/presence/{clientId}`): no `/`, `+`, `#`, whitespace.
 */
export function sanitizeInstanceId(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, "");
}

/** Booleans describing the current execution world; injected so the resolver is pure/testable. */
export interface ExecutionContextFlags {
  isContentScript: boolean; // extension content script running inside a web page
  isBackground: boolean;    // background service worker / page
  isPopup: boolean;         // extension action popup
  isOffscreen: boolean;     // offscreen document
  isExtension: boolean;     // any extension context (chrome.runtime.id present)
}

/**
 * Map the current execution world to a short, topic-safe discriminator. Two worlds in the SAME
 * tab share sessionStorage and would otherwise resolve the same clientId; the tag keeps them
 * distinct so they never take each other's MQTT session over.
 */
export function resolveExecutionContextTag(flags: ExecutionContextFlags): string {
  if (flags.isContentScript) return "content";
  if (flags.isBackground) return "sw";
  if (flags.isPopup) return "popup";
  if (flags.isOffscreen) return "offscreen";
  return "page";
}

/**
 * Resolve a stable instance id for this surface.
 * - gateway: reuse the already-stable per-pod `localStorage.sessionId`.
 * - frontend / whatsapp (and gateway when sessionId is absent): a per-REALM UUID
 *   held in the injected session slot, so repeated connect()/reconnects within
 *   one page load reuse one id.
 *
 * IMPORTANT: the session slot must be UNIQUE PER EXECUTION CONTEXT, never shared
 * across tabs. The browser glue below backs it with per-realm in-memory storage
 * rather than `sessionStorage` precisely because `sessionStorage` is *copied*
 * into a child tab on duplicate / link / window.open — two tabs would then
 * resolve the SAME clientId, and EMQX (one connection per clientId) would kick
 * them in an endless takeover war (connect -> "Connection closed" -> reconnect).
 * A reload mints a fresh id; the prior session's presence is cleared by the LWT.
 */
export function resolveStableInstanceId(clientType: ClientType, store: InstanceIdStore): string {
  if (clientType === ClientType.GATEWAY) {
    const sessionId = store.getLocal("sessionId");
    if (sessionId) return sanitizeInstanceId(sessionId);
  }

  const existing = store.getSession(MQTT_INSTANCE_ID_KEY);
  if (existing) return existing;

  const fresh = sanitizeInstanceId(store.genId());
  store.setSession(MQTT_INSTANCE_ID_KEY, fresh);
  return fresh;
}

// ---------------------------------------------------------------------------
// Thin glue: wire the real browser globals into the resolver.
//
// The session slot is backed by this MODULE-LEVEL in-memory map, NOT by
// sessionStorage. A fresh JS realm (every page load, and each isolated world —
// page main-world vs extension content script) re-initialises this map empty,
// so the id is unique per execution context and stable for the lifetime of that
// load (reconnects reuse it). Crucially it is NEVER copied into a duplicated
// tab the way sessionStorage is, which is what previously caused two tabs to
// share a clientId and fight an EMQX takeover war. localStorage is still read
// directly for the gateway's per-pod sessionId (a deliberate cross-realm pod
// identity, and gateway pods don't duplicate tabs).
// ---------------------------------------------------------------------------

const realmInstanceStore: Record<string, string> = {};

function browserStore(): InstanceIdStore {
  return {
    getSession: (key) => (key in realmInstanceStore ? realmInstanceStore[key] : null),
    setSession: (key, value) => void (realmInstanceStore[key] = value),
    getLocal: (key) => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    genId: () => {
      try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
          return crypto.randomUUID();
        }
      } catch {
        /* fall through */
      }
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    },
  };
}

/** Stable per-surface instance id using the real browser globals. */
export function getStableInstanceId(clientType: ClientType): string {
  return resolveStableInstanceId(clientType, browserStore());
}

/** Stable execution-context tag using the real environment detectors. */
export function getExecutionContextTag(): string {
  const isExtension = isExtensionContext();
  const href = (typeof globalThis !== "undefined" && globalThis.location?.href) || "";
  return resolveExecutionContextTag({
    isExtension,
    isContentScript: isContentScript(),
    isBackground: isBackgroundWorker() || isBackgroundPage(),
    isPopup: isInPopup() === true,
    isOffscreen: isExtension && href.includes("offscreen"),
  });
}
